import React, {useState, ChangeEvent} from 'react';
import {CreateExerciseProps} from './CreateExercise.type';
import {DirectUpload} from '../../media/DirectUpload';
import {SelectStyled} from '../../../elements/forms/InputContainer';
import {
  CreateExerciseForm,
  VideoUploadContainer,
  VideoPreview,
  FormFields,
  CreateExerciseActions,
  UploadProgress,
  UploadStatus,
} from '../ExerciseLibraryModal/ExerciseLibraryModal.style';
import {
  FormField,
  FormLabel,
  FormInput,
  FormTextarea,
  Button,
} from '../ExerciseLibraryModal/ExerciseLibraryModal.style';

export const CreateExercise = ({
  formData,
  isCreating,
  isVideoUploading,
  videoUploadUrl,
  onFormDataChange,
  onVideoUpload,
  onVideoUploadStatus,
  onCreate,
  onCancel,
}: CreateExerciseProps): JSX.Element => {
  const [uploadProgress, setUploadProgress] = useState(0);

  const exerciseTypeOptions = [
    {value: '', label: 'Selecione o tipo de exercício'},
    {value: 'Carga', label: 'Carga'},
    {value: 'Tempo', label: 'Tempo'},
    {value: 'Repetição', label: 'Repetição'},
    {value: 'Distância', label: 'Distância'},
    {value: 'Calorias', label: 'Calorias'},
  ];

  const handleInputChange =
    (field: keyof CreateExerciseProps['formData']) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onFormDataChange({[field]: event.target.value});
    };

  const handleCheckboxChange =
    (field: keyof CreateExerciseProps['formData']) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      onFormDataChange({[field]: event.target.checked});
    };

  const handleSelectChange = (selectedOption: any) => {
    onFormDataChange({exerciseType: selectedOption?.value || undefined});
  };

  const handleCreateExercise = async () => {
    const success = await onCreate();
    if (success) {
      // Reset form after successful creation
      onFormDataChange({
        name: '',
        description: '',
        recordEnabled: false,
        exerciseType: undefined,
        videoUrl: '',
      });
    }
  };

  const canSubmit = formData.name && formData.description && !isCreating;

  const renderVideoUpload = () => {
    const hasVideo = !!videoUploadUrl;
    const containerClass = isVideoUploading
      ? 'uploading'
      : hasVideo
      ? 'has-video'
      : '';

    return (
      <VideoUploadContainer className={containerClass}>
        <DirectUpload
          mediaType="video"
          folder="exercises/videos"
          onUploadComplete={onVideoUpload}
          onUploadStatusChange={onVideoUploadStatus}>
          <div>
            {isVideoUploading ? (
              <div>
                <i
                  className="far fa-spinner fa-spin"
                  style={{fontSize: '2rem', color: '#2196f3'}}
                />
                <p>Enviando vídeo...</p>
                {uploadProgress > 0 && (
                  <UploadProgress>
                    <div
                      className="progress-bar"
                      style={{width: `${uploadProgress}%`}}
                    />
                  </UploadProgress>
                )}
              </div>
            ) : hasVideo ? (
              <div>
                <i
                  className="far fa-check-circle"
                  style={{fontSize: '2rem', color: '#4caf50'}}
                />
                <p>Vídeo enviado com sucesso!</p>
                <VideoPreview>
                  <video src={videoUploadUrl} controls />
                </VideoPreview>
              </div>
            ) : (
              <div>
                <i
                  className="far fa-cloud-upload"
                  style={{fontSize: '2rem', color: '#666'}}
                />
                <p>Clique para selecionar ou arraste seu vídeo aqui</p>
                <small>Formatos: MP4, MOV, AVI (máx. 100MB)</small>
              </div>
            )}
          </div>
        </DirectUpload>
      </VideoUploadContainer>
    );
  };

  return (
    <CreateExerciseForm>
      <div style={{marginBottom: '20px'}}>
        <h3>Criar Novo Exercício</h3>
        <p style={{color: '#666', fontSize: '0.9rem'}}>
          Envie um vídeo e preencha as informações para criar um novo exercício.
        </p>
      </div>

      {renderVideoUpload()}

      <FormFields>
        <FormField>
          <FormLabel htmlFor="exercise-name">
            Nome do Exercício <span style={{color: 'red'}}>*</span>
          </FormLabel>
          <FormInput
            id="exercise-name"
            type="text"
            value={formData.name}
            onChange={handleInputChange('name')}
            placeholder="Ex: Flexão de braço"
            required
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="exercise-description">
            Descrição/Instruções <span style={{color: 'red'}}>*</span>
          </FormLabel>
          <FormTextarea
            id="exercise-description"
            value={formData.description}
            onChange={handleInputChange('description')}
            placeholder="Descreva como executar o exercício, posicionamento, respiração..."
            rows={4}
            required
          />
        </FormField>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '15px',
          }}>
          <FormField>
            <label style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <input
                type="checkbox"
                checked={formData.recordEnabled}
                onChange={handleCheckboxChange('recordEnabled')}
              />
              <span>Habilitar para recordes</span>
            </label>
            <small style={{color: '#666', display: 'block', marginTop: '4px'}}>
              Estudantes poderão criar recordes pessoais neste exercício
            </small>
          </FormField>

          {formData.recordEnabled && (
            <FormField>
              <SelectStyled
                label="Tipo de exercício"
                options={exerciseTypeOptions}
                value={exerciseTypeOptions.find(
                  (option) => option.value === formData.exerciseType,
                )}
                onChange={handleSelectChange}
                placeholder="Selecione o tipo de exercício"
                isClearable
              />
            </FormField>
          )}
        </div>
      </FormFields>

      <CreateExerciseActions>
        <Button variant="secondary" onClick={onCancel} disabled={isCreating}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleCreateExercise}
          disabled={!canSubmit}>
          {isCreating ? (
            <>
              <i
                className="far fa-spinner fa-spin"
                style={{marginRight: '8px'}}
              />
              Criando...
            </>
          ) : (
            'Criar Exercício'
          )}
        </Button>
      </CreateExerciseActions>

      {!canSubmit && (
        <UploadStatus style={{textAlign: 'center', marginTop: '10px'}}>
          {!formData.name && 'Nome é obrigatório. '}
          {!formData.description && 'Descrição é obrigatória. '}
        </UploadStatus>
      )}
    </CreateExerciseForm>
  );
};
